import React, { FC, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import CustomerInformationDrawer from "../CustomerInformationDrawer";
import useNormalizedCustomerDrawerData from "./useNormalizedCustomerDrawerData";
import useCustomerFormik from "./useCustomerFormik";
import useEditMode from "./useEditMode";
import useCustomer from "./useCustomer";
import { useAppContext } from "../../Unknown/AppContext";

export type CustomerInformationDrawerContainerProps = {
  isOtherPerson?: boolean;
  customerId?: string;
  isOpen: boolean;
  handleClose: () => void;
  handlePostSubmit?: () => void | Promise<void>;
  handlePatientClick: (patientMrn: string) => void;
};

const MERGE_ALLOWED_ROLES = ["regional_admin", "super_admin"] as const;

const CustomerInformationDrawerContainer: FC<
  CustomerInformationDrawerContainerProps
> = ({
  isOtherPerson,
  customerId,
  isOpen,
  handleClose,
  handlePatientClick,
  handlePostSubmit,
}) => {
  const {
    isLoading,
    error,
    data: customer,
    refetch,
  } = useCustomer({ customerId, isOtherPerson });

  const { isEditMode, handleEditModeChange } = useEditMode();
  const navigate = useNavigate();
  const location = useLocation();

  const { databaseUserData } = useAppContext();
  const canMerge =
    !isOtherPerson &&
    MERGE_ALLOWED_ROLES.some((role) =>
      databaseUserData.permissions.includes(role),
    );

  const normalizedCustomerData = useNormalizedCustomerDrawerData({
    customer,
    isOtherPerson,
    handlePatientClick,
  });

  const onSubmitSuccess = useCallback(async () => {
    await refetch();

    handleEditModeChange(false);

    if (handlePostSubmit) await handlePostSubmit();
  }, [handleEditModeChange, handlePostSubmit, refetch]);

  const { formikConfig: customerFormikConfig } = useCustomerFormik({
    customer,
    customerId,
    isOtherPerson,
    onSubmitSuccess,
  });

  const handleMergeClick = useCallback(() => {
    navigate(`${location.pathname}/merge`);
  }, [location.pathname, navigate]);

  return (
    <Formik {...customerFormikConfig}>
      <CustomerInformationDrawer
        isOpen={isOpen}
        handleClose={handleClose}
        isLoading={isLoading}
        error={error}
        data={normalizedCustomerData}
        isEditMode={isEditMode}
        handleEditModeChange={handleEditModeChange}
        canMerge={canMerge}
        onMergeClick={handleMergeClick}
      />
    </Formik>
  );
};

export default CustomerInformationDrawerContainer;
